(function (globalScope) {
  'use strict';

  const RECOVERY_STORAGE_KEY = 'pitchprint-project-recovery-v1';
  const PITCHPRINT_TEMP_SAVE_KEY = 'pitchPrintTempSave';
  const PITCHPRINT_CLIENT_URL = 'https://pitchprint.io/rsc/js/client.js';
  const PROJECT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9:._-]{15,127}$/;
  const VARIANT_ID_PATTERN = /^\d{6,24}$/;

  function normalizePages(value) {
    const parsed = Number.parseInt(String(value || '1'), 10);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 100 ? parsed : 1;
  }

  function normalizeProductPath(value, currentOrigin, shopDomain) {
    const rawValue = String(value || '').trim();
    if (!rawValue) throw new Error('product_missing');

    let candidate = rawValue;
    if (!candidate.startsWith('/') && !/^https?:\/\//i.test(candidate)) {
      candidate = `https://${candidate}`;
    }

    const url = new URL(candidate, currentOrigin);
    const allowedHosts = new Set([
      new URL(currentOrigin).host.toLowerCase(),
      String(shopDomain || '').toLowerCase()
    ]);

    if (!allowedHosts.has(url.host.toLowerCase())) {
      throw new Error('product_host_invalid');
    }

    if (!url.pathname.includes('/products/')) {
      throw new Error('product_path_invalid');
    }

    return `${url.pathname}${url.search}`;
  }

  function normalizeRecovery(input, options) {
    const projectId = String(input.projectId || '').trim();
    if (!PROJECT_ID_PATTERN.test(projectId)) throw new Error('project_invalid');

    const variant = String(input.variant || '').trim();
    if (variant && !VARIANT_ID_PATTERN.test(variant)) throw new Error('variant_invalid');

    return {
      projectId,
      product: normalizeProductPath(
        input.product,
        options.currentOrigin,
        options.shopDomain
      ),
      numPages: normalizePages(input.numPages),
      variant
    };
  }

  function parseRecoveryHash(hash, options) {
    const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
    if (!params.has('projectId')) return null;

    return normalizeRecovery(
      {
        projectId: params.get('projectId'),
        product: params.get('product'),
        numPages: params.get('numPages'),
        variant: params.get('variant')
      },
      options
    );
  }

  function serializeRecovery(recovery) {
    const params = new URLSearchParams();
    params.set('projectId', recovery.projectId);
    params.set('product', recovery.product);
    params.set('numPages', String(recovery.numPages));
    if (recovery.variant) params.set('variant', recovery.variant);
    return params.toString();
  }

  function buildProductResumeUrl(recovery, currentOrigin) {
    const target = new URL(recovery.product, currentOrigin);
    if (recovery.variant) target.searchParams.set('variant', recovery.variant);
    target.searchParams.set('resume', 'true');
    target.searchParams.set('projectId', recovery.projectId);
    target.searchParams.set('numPages', String(recovery.numPages));
    return target.toString();
  }

  function projectMatches(project, projectId) {
    return [project && project.id, project && project._id, project && project.projectId]
      .filter(Boolean)
      .some((value) => String(value) === projectId);
  }

  function loadScript(documentObject, source) {
    if (globalScope.PitchPrintClient) return Promise.resolve();

    const existing = documentObject.querySelector(`script[src="${source}"]`);
    if (existing) {
      return new Promise((resolve, reject) => {
        if (globalScope.PitchPrintClient) {
          resolve();
          return;
        }
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = documentObject.createElement('script');
      script.src = source;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      documentObject.head.appendChild(script);
    });
  }

  function initializeRecovery(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const configNode = root.querySelector('[data-pitchprint-recovery-config]');
    const accountButton = root.querySelector('[data-pitchprint-recovery-account]');
    const guestButton = root.querySelector('[data-pitchprint-recovery-guest]');
    const actions = root.querySelector('[data-pitchprint-recovery-actions]');
    const status = root.querySelector('[data-pitchprint-recovery-status]');
    const details = root.querySelector('[data-pitchprint-recovery-details]');
    const recentProjects = root.querySelector('#pp_mydesigns_div');

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      status.textContent = 'Impossibile inizializzare la pagina di recupero.';
      status.dataset.state = 'error';
      return;
    }

    const options = {
      currentOrigin: globalScope.location.origin,
      shopDomain: config.shopDomain
    };

    let recovery = null;
    try {
      recovery = parseRecoveryHash(globalScope.location.hash, options);
      if (recovery) {
        globalScope.sessionStorage.setItem(
          RECOVERY_STORAGE_KEY,
          JSON.stringify(recovery)
        );
        const cleanUrl = new URL(globalScope.location.href);
        cleanUrl.hash = '';
        cleanUrl.searchParams.set('recovery', '1');
        globalScope.history.replaceState(
          {},
          '',
          `${cleanUrl.pathname}${cleanUrl.search}`
        );
      } else if (new URLSearchParams(globalScope.location.search).get('recovery') === '1') {
        const stored = globalScope.sessionStorage.getItem(RECOVERY_STORAGE_KEY);
        if (stored) recovery = normalizeRecovery(JSON.parse(stored), options);
      }
    } catch (error) {
      recovery = null;
    }

    const setBusy = (isBusy) => {
      accountButton.disabled = isBusy;
      guestButton.disabled = isBusy;
      root.setAttribute('aria-busy', String(isBusy));
    };

    const setStatus = (message, state) => {
      status.textContent = message;
      status.dataset.state = state || 'info';
    };

    const clearTemporaryClaim = () => {
      try {
        globalScope.localStorage.removeItem(PITCHPRINT_TEMP_SAVE_KEY);
      } catch (error) {
        // Storage can be unavailable in strict privacy modes.
      }
    };

    const redirectToProduct = () => {
      setBusy(true);
      setStatus(config.labels.opening, 'loading');
      globalScope.sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
      globalScope.location.assign(
        buildProductResumeUrl(recovery, globalScope.location.origin)
      );
    };

    if (!recovery) {
      setStatus(config.labels.invalid, 'error');
      actions.hidden = true;
      details.hidden = true;
      return;
    }

    actions.hidden = false;
    details.hidden = false;
    setStatus(config.labels.ready, 'success');

    guestButton.addEventListener('click', redirectToProduct);

    accountButton.addEventListener('click', async () => {
      if (!config.customerId) {
        const loginUrl = new URL(config.loginUrl, globalScope.location.origin);
        const returnHash = serializeRecovery(recovery);
        const returnTo = `${globalScope.location.pathname}${globalScope.location.search}#${returnHash}`;
        loginUrl.searchParams.set('return_to', returnTo);
        setBusy(true);
        setStatus(config.labels.redirectingToLogin, 'loading');
        globalScope.location.assign(loginUrl.toString());
        return;
      }

      setBusy(true);
      setStatus(config.labels.associating, 'loading');

      try {
        globalScope.localStorage.setItem(PITCHPRINT_TEMP_SAVE_KEY, recovery.projectId);
        await loadScript(globalScope.document, PITCHPRINT_CLIENT_URL);

        if (!globalScope.PitchPrintClient) throw new Error('client_unavailable');

        let completed = false;
        const timeout = globalScope.setTimeout(() => {
          if (completed) return;
          completed = true;
          clearTemporaryClaim();
          setBusy(false);
          setStatus(config.labels.associationFailed, 'error');
        }, 20000);

        globalScope.PPCLIENT = globalScope.PPCLIENT || {};

        const client = new globalScope.PitchPrintClient({
          userId: String(config.customerId),
          userData: config.customerData,
          langCode: globalScope.document.documentElement.lang || 'it',
          mode: 'edit',
          apiKey: config.shopDomain,
          client: 'sp',
          afterValidation: '_fetchProjects'
        });

        globalScope.ppclient = client;
        client.on('after-fetch-projects', () => {
          if (completed) return;
          completed = true;
          globalScope.clearTimeout(timeout);
          clearTemporaryClaim();

          const projects = Array.isArray(client.vars && client.vars.projects)
            ? client.vars.projects
            : [];

          if (!projects.some((project) => projectMatches(project, recovery.projectId))) {
            setBusy(false);
            setStatus(config.labels.associationFailed, 'error');
            return;
          }

          if (recentProjects) recentProjects.replaceChildren();
          redirectToProduct();
        });
      } catch (error) {
        clearTemporaryClaim();
        setBusy(false);
        setStatus(config.labels.associationFailed, 'error');
      }
    });
  }

  function initializeAll(scope) {
    scope
      .querySelectorAll('[data-pitchprint-project-recovery]')
      .forEach(initializeRecovery);
  }

  const api = {
    buildProductResumeUrl,
    normalizeRecovery,
    parseRecoveryHash,
    serializeRecovery
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (globalScope && globalScope.document) {
    globalScope.PitchPrintProjectRecovery = api;

    if (globalScope.document.readyState === 'loading') {
      globalScope.document.addEventListener(
        'DOMContentLoaded',
        () => initializeAll(globalScope.document),
        { once: true }
      );
    } else {
      initializeAll(globalScope.document);
    }

    globalScope.document.addEventListener('shopify:section:load', (event) => {
      initializeAll(event.target);
    });
  }
})(typeof window !== 'undefined' ? window : null);
